import React, { useCallback, useEffect, useState } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import Form, { useForm } from 'src/presentation/components/forms/Form';
import InputField from 'src/presentation/components/forms/InputField';
import emailDomainsResource from 'src/actions/email/emailDomainsResource';
import generateId from 'src/lib/generateId';
import { DefaultEmailDomainConfiguration } from 'src/models/Email/EmailDomain';
import { STATUS_FAILED, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import AddEmailError from 'src/presentation/components/pages/email/components/AddEmailError';

export const useAddEmailDomain = ({ errors, onSaveSuccess }) => {
  const dispatch = useDispatch();
  const resourceValues = useResourceValues(emailDomainsResource);
  const { persistingState } = resourceValues;
  const [pendingAdd, setPendingAdd] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [showError, setShowError] = useState(false);

  const { validate, add } = emailDomainsResource.effects;
  const { resetErrors } = emailDomainsResource.actions as any;

  const formContext = useForm({
    onSave: ({ workingState }) => {
      const isValid = !!dispatch(validate(workingState));
      if (isValid) {
        const domainName = get(workingState, 'content.customerDomain');
        dispatch(
          add(
            {},
            { ...workingState, id: generateId() },
            {
              disableWindowBack: true,
              customToastMsg: `Email domain "${domainName}" saved`,
              withoutToastOnError: true,
              timeout: 30000,
            }
          )
        );
        setPendingData({ ...workingState });
        setPendingAdd(true);
        setShowError(false);
      }
    },
    item: DefaultEmailDomainConfiguration,
    errors,
    resetErrors,
  });

  const { onSave, updateState } = formContext;

  const clearEmailDomainWorkingState = useCallback((): void => {
    updateState(DefaultEmailDomainConfiguration);
    dispatch(resetErrors());
    setShowError(false);
  }, [updateState, dispatch, resetErrors]);

  useEffect(() => {
    if (pendingAdd) {
      if (persistingState === STATUS_SUCCEEDED) {
        onSaveSuccess(pendingData);
        clearEmailDomainWorkingState();
        setPendingAdd(false);
        setPendingData(null);
      } else if (persistingState === STATUS_FAILED) {
        setShowError(true);
        setPendingAdd(false);
        setPendingData(null);
      }
    }
  }, [persistingState, pendingAdd, pendingData, onSaveSuccess, clearEmailDomainWorkingState]);

  const renderEmailDomainForm = (): JSX.Element => {
    return (
      <Form context={formContext} preventSaveOnEnter>
        <StyledInputField
          action={<StyledAtSymbol>@</StyledAtSymbol>}
          className="domainName"
          data-aid="domainName"
          attr="content.customerDomain"
          label=""
          placeholder="gladly.com"
        />
        {showError && <AddEmailError />}
      </Form>
    );
  };

  return { clearEmailDomainWorkingState, onEmailDomainSave: onSave, renderEmailDomainForm, isLoading: pendingAdd };
};

const StyledAtSymbol = styled.span`
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  font-weight: ${({ theme }) => theme.fontWeight.heavy};
`;

const StyledInputField = styled(InputField)`
  position: relative;
  text-align: left;
  min-width: 360px;

  div {
    position: relative;
  }

  input {
    padding-left: 18px;
  }
`;
